import React, { Suspense, useLayoutEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoutes from './ProtectedRoutes';
import RequireCapability from './RequireCapability';
import { applyViewportFor } from '../shared/utils/desktopViewport';
import { lazyWithReload } from '../shared/utils/lazyWithReload';
import Login from '../pages/Login';
const TrackComplaint = lazyWithReload(() => import('../pages/TrackComplaint'));
const OrderPage = lazyWithReload(() => import('../pages/OrderPage'));
const MyOrders = lazyWithReload(() => import('../pages/MyOrders'));
const HandoverScanPage = lazyWithReload(() => import('../features/handover/ui/HandoverScanPage'));
const PrintCentrePage = lazyWithReload(() => import('../features/handover/ui/PrintCentrePage'));
const QcPage = lazyWithReload(() => import('../features/qc/ui/QcPage'));

import DashboardLayout from '../features/dashboard/ui/DashboardLayout';

/**
 * track.dakshaorganics.com is the address printed in our DLT order-confirmation SMS,
 * so its BARE root has to be the order-tracking page — a customer who taps that link
 * must not land on a staff login. The domain points at this same Hosting site, so the
 * host is what tells us which front door was used.
 */
const isTrackingDomain = () => {
    try { return /^track\./i.test(String(window.location.hostname || '')); }
    catch { return false; }
};

const AppRouter = () => {
    const { pathname } = useLocation();
    const trackingDomain = isTrackingDomain();

    // Phones get the full desktop layout on staff screens — see desktopViewport.
    useLayoutEffect(() => { applyViewportFor(pathname); }, [pathname]);

    const handleLogout = async () => {
        try {
            const { getAuth, signOut } = await import('firebase/auth');
            const auth = getAuth();
            await signOut(auth);
            window.location.href = '/login';
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    return (
        <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
            <Routes>
                {/* On track.* the root IS the tracking page. Everywhere else "/" keeps
                    its normal behaviour (login / dashboard) — see isTrackingDomain. */}
                {trackingDomain && <Route path="/" element={<MyOrders />} />}
                <Route path="/login" element={<Login />} />
                {/* PUBLIC customer self-service complaint tracking — no auth */}
                <Route path="/track" element={<TrackComplaint />} />
                {/* PUBLIC quick-order storefront — no auth */}
                <Route path="/order" element={<OrderPage />} />
                {/* PUBLIC "Track My Order" (mobile + OTP) — no auth */}
                <Route path="/my-orders" element={<MyOrders />} />

                <Route element={<ProtectedRoutes />}>
                    {/* Handover scan — the same full-screen page on phone and desktop.
                        Warehouse staff land here; it is not a dashboard tab. */}
                    {/* Handover desk — gated by capability so a pasted link does not
                        open it. The endpoints behind it check the role server-side too. */}
                    <Route element={<RequireCapability capability="canViewHandover" />}>
                        <Route path="/handover" element={<HandoverScanPage />} />
                        <Route path="/handover/print" element={<PrintCentrePage />} />
                    </Route>
                    {/* Parcel QC — full-screen like the handover desk; the QC inspector's only page. */}
                    <Route element={<RequireCapability capability="canViewQc" />}>
                        <Route path="/qc" element={<QcPage />} />
                    </Route>
                    {/*
                      * ONE route table for phone and desktop.
                      *
                      * There used to be a separate six-route mobile table with a
                      * catch-all to the mobile dashboard, which meant that on a phone
                      * Rewards, Quick Order, the courier desks, complaints, tracking,
                      * staff - most of the business - did not exist: tapping them in any
                      * menu silently landed back on the dashboard. The app cannot be
                      * "managed from a phone" while the router refuses to route.
                      *
                      * Phones now get exactly the desktop screens, laid out at desktop
                      * width (see desktopViewport) — same layout, same functions.
                      */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardLayout initialTab="dashboard" onLogout={handleLogout} />} />
                    <Route path="/customers" element={<DashboardLayout initialTab="customers" onLogout={handleLogout} />} />
                    <Route path="/customers/:customerId" element={<DashboardLayout initialTab="customers" onLogout={handleLogout} />} />
                    <Route path="/telecaller" element={<DashboardLayout initialTab="telecaller" onLogout={handleLogout} />} />
                    <Route path="/chat" element={<DashboardLayout initialTab="chat" onLogout={handleLogout} />} />
                    <Route path="/orders" element={<DashboardLayout initialTab="orders" onLogout={handleLogout} />} />
                    <Route path="/products" element={<DashboardLayout initialTab="products" onLogout={handleLogout} />} />
                    <Route path="/settings" element={<DashboardLayout initialTab="settings" onLogout={handleLogout} />} />
                    <Route path="/parser" element={<DashboardLayout initialTab="parser" onLogout={handleLogout} />} />
                    <Route path="/dtdc" element={<DashboardLayout initialTab="dtdc" onLogout={handleLogout} />} />
                    <Route path="/india-post" element={<DashboardLayout initialTab="india-post" onLogout={handleLogout} />} />
                    <Route path="/shiprocket" element={<DashboardLayout initialTab="shiprocket" onLogout={handleLogout} />} />
                    <Route path="/ekart" element={<DashboardLayout initialTab="ekart" onLogout={handleLogout} />} />
                    <Route path="/archived" element={<DashboardLayout initialTab="archived" onLogout={handleLogout} />} />
                    <Route path="/cancelled" element={<DashboardLayout initialTab="cancelled" onLogout={handleLogout} />} />
                    <Route path="/abandoned" element={<DashboardLayout initialTab="abandoned" onLogout={handleLogout} />} />
                    <Route path="/tracking" element={<DashboardLayout initialTab="tracking" onLogout={handleLogout} />} />
                    <Route path="/referrals" element={<DashboardLayout initialTab="referrals" onLogout={handleLogout} />} />
                    <Route path="/rewards" element={<DashboardLayout initialTab="rewards" onLogout={handleLogout} />} />
                    <Route path="/calendar" element={<DashboardLayout initialTab="calendar" onLogout={handleLogout} />} />
                    <Route path="/staff" element={<DashboardLayout initialTab="staff" onLogout={handleLogout} />} />
                    <Route path="/complaints" element={<DashboardLayout initialTab="complaints" onLogout={handleLogout} />} />
                    <Route path="/quick-order" element={<DashboardLayout initialTab="quick-order" onLogout={handleLogout} />} />
                    <Route path="/escalations" element={<DashboardLayout initialTab="escalations" onLogout={handleLogout} />} />
                    <Route path="/approvals" element={<DashboardLayout initialTab="approvals" onLogout={handleLogout} />} />
                    {/* Which module is spending the Firebase bill. */}
                    <Route path="/data-usage" element={<DashboardLayout initialTab="data-usage" onLogout={handleLogout} />} />
                    <Route path="/backup" element={<DashboardLayout initialTab="backup" onLogout={handleLogout} />} />
                    <Route path="/todo" element={<DashboardLayout initialTab="todo" onLogout={handleLogout} />} />
                    <Route path="/insights" element={<DashboardLayout initialTab="insights" onLogout={handleLogout} />} />
                    <Route path="*" element={<div>Page Not Found</div>} />
                </Route>
            </Routes>
        </Suspense>
    );
};

export default AppRouter;
